import { useState, useRef, useEffect } from "react";
import { animate } from "motion";
import { useInView, motion } from "motion/react";
import { AnimVals } from "@/utils/animUtils";
import { Button } from "@components";
import { getHighlightMarkup } from "@/core/utils";
import { useViewSize } from '@/providers/ViewSizeContext';

import FormInput from "./FormInput";
import CheckYes from "@/assets/svg/check-yes.svg";

import classNames from "classnames/bind";
import styles from './Footer.module.scss';
import { usePageWrapperData } from "@/providers/PageWrapperContext";
const cx = classNames.bind(styles);

type NewsletterFormProps = FooterSettings["footerSettingsFields"]["newsletterForm"];

export default function NewsletterForm({ title, eyebrow, form }: NewsletterFormProps) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: ""
  });

  const headlineMarkup = getHighlightMarkup(title);
  const { labels: { newsletterFormGlobal } = {} } = usePageWrapperData();

  const [requestStatus, setRequestStatus] = useState<{
    loading: boolean;
    success: boolean | null;
    message?: string;
    sub?: string;
    error?: string;
  }>({
    loading: false,
    success: null,
  });

  const onFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (event && event.preventDefault) event.preventDefault();
    const formElement = event.currentTarget;
    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    setRequestStatus({ loading: true, success: null });

    try {
      const response = await fetch('/api/subscribe/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.success) {
        setRequestStatus({
          loading: false,
          success: true,
          message: newsletterFormGlobal?.success.message,
          sub: newsletterFormGlobal?.success.sub,
        });
        setFormData({ first_name: "", last_name: "", email: "" });
      } else {
        setRequestStatus({
          loading: false,
          success: false,
          message: newsletterFormGlobal?.error,
        });
      }
    } catch (error) {
      setRequestStatus({
        loading: false,
        success: false,
        error: newsletterFormGlobal?.serverError,
      });
    } finally {
      setRequestStatus(prev => ({
        ...prev,
        loading: false
      }));
      // Reset form fields after submission
      if (formElement) formElement.reset();
    }
  }

  const onFormInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  }

  // fade in animation for form and success card
  const formRef = useRef<HTMLFormElement | null>(null);
  const successCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (formRef.current) {
      animate(formRef.current, { opacity: 1 }, { duration: 0.2 });
    }
  }, [formRef.current]);

  useEffect(() => {
    if (successCardRef.current) {
      animate(successCardRef.current, { opacity: 1 }, { duration: 0.2 });
    }
  }, [successCardRef.current, requestStatus]);

  // -------------------- Animate In

  const { isDesktop } = useViewSize();
  const rootEl = useRef<HTMLDivElement>(null);
  const isRevealEnabled = false; // TODO: Remove reveal animation code later if not needed -Gabe
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  const ANIM_DIST = AnimVals.sm.distance;
  const ANIM_DUR = AnimVals.sm.duration;
  const DELAY_INC = AnimVals.sm.delay;

  let animInDelay = 0; // Delay initial

  const createMotionProps = (delay: number = 0) => ({
    initial: isRevealEnabled ? {
      opacity: AnimVals.opacity,
      y: ANIM_DIST
    } : {},
    animate: isInView ? {
      opacity: 1,
      y: 0
    } : {},
    transition: {
      duration: ANIM_DUR,
      ease: AnimVals.ease,
      delay
    }
  });

  // Eyebrow
  const motionProps_Eyebrow = createMotionProps(animInDelay);
  animInDelay += DELAY_INC; // Delay increment

  // Headline
  const motionProps_Headline = createMotionProps(animInDelay);
  animInDelay += DELAY_INC; // Delay increment

  // Form Wrapper
  const motionProps_FormWrapper = createMotionProps(animInDelay);

  // -------------------- Render

  return (
    <div className={cx("newsletter-form")} ref={rootEl}>

      <div className={cx("newsletter-form__copy")}>
        <motion.p className={cx("eyebrow-l", "eyebrow")} {...motionProps_Eyebrow}>{eyebrow}</motion.p>
        <motion.h2 className={cx("title-uc-l")} dangerouslySetInnerHTML={{ __html: headlineMarkup }} {...motionProps_Headline} />
      </div>

      <motion.div className={cx("form-wrapper")} {...motionProps_FormWrapper}>
      {requestStatus.success ?
        (
          <div className={cx("success-card")} ref={successCardRef}>
            <CheckYes className={cx("icon")} />
            <div className={cx("success-card__message")}>
              <h4 className={cx("text-l", "text-center")}>{requestStatus.message}</h4>
              <p className={cx("text-m", "text-center")}>{requestStatus.sub}</p>
            </div>
          </div>
        ) : (
        <form className={cx("form")} onSubmit={onFormSubmit} ref={formRef}>
          <FormInput
            name="first_name"
            placeholder={form.firstName}
            value={formData.first_name}
            width="half"
            onChange={onFormInputChange}
          />
          <FormInput
            name="last_name"
            placeholder={form.lastName}
            value={formData.last_name}
            width="half"
            onChange={onFormInputChange}
          />
          <FormInput
            name="email"
            type="email"
            placeholder={form.email}
            value={formData.email}
            width="full"
            onChange={onFormInputChange}
          />
          <div className={cx("submit-wrapper")}>
            {requestStatus.success === false && <h5 className={cx("text-m", "error-message")}>An unexpected error has occurred. Please try again later.</h5>}
            <Button className={cx("submit")} label={form.submit} />
          </div>
        </form>
      )}
      </motion.div>

    </div>
  );

}
